import type { FileAttrs } from "./extensions/file";

/** Display fields produced by preset `formatMention` for an entity hit. */
export type FormattedMention = {
  id: string;
  label: string;
  href?: string;
  description?: string;
  image?: string;
};

export type MentionKindConfig = {
  char: string;
  name: string;
  filter?: (mention: FormattedMention, query: string) => boolean;
  formatMention: (item: any) => FormattedMention;
};

export type UploadFileFn = (
  file: File,
  uploadOptions: { forType: string; forId: string },
) => Promise<string>;

export type EditorCapabilities = {
  heading?: { levels?: Array<1 | 2 | 3 | 4 | 5 | 6> };
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  bulletList?: boolean;
  orderedList?: boolean;
  link?: { autoLink?: boolean };
  alignment?: boolean;
  mentions?: MentionKindConfig[];
  /** Enables `:` emoji suggestions (data comes from `@emoji-mart/data`). */
  emoji?: boolean;
  image?: { uploadFile: UploadFileFn };
  video?: { srcRegex?: RegExp };
  iframe?: boolean;
  table?: boolean;
  file?: {
    uploadFile: UploadFileFn;
    onClick?: (src: string | null, attrs: FileAttrs) => void;
  };
};

/** Keys that can be toggled via `customPresetExtensions` / `customPresetMenu`. */
export type CapabilityName = keyof EditorCapabilities | "mention";

/** Names present on a capabilities object (for custom-preset filtering). */
export const getCapabilityNames = (
  capabilities: EditorCapabilities,
): CapabilityName[] => {
  const names: CapabilityName[] = [];
  if (capabilities.heading) names.push("heading");
  if (capabilities.bold) names.push("bold");
  if (capabilities.italic) names.push("italic");
  if (capabilities.underline) names.push("underline");
  if (capabilities.strike) names.push("strike");
  if (capabilities.bulletList) names.push("bulletList");
  if (capabilities.orderedList) names.push("orderedList");
  if (capabilities.link) names.push("link");
  if (capabilities.alignment) names.push("alignment");
  if (capabilities.mentions?.length) names.push("mention");
  if (capabilities.emoji) names.push("emoji");
  if (capabilities.image) names.push("image");
  if (capabilities.video) names.push("video");
  if (capabilities.iframe) names.push("iframe");
  if (capabilities.table) names.push("table");
  if (capabilities.file) names.push("file");
  return names;
};

/** Keep only the capability keys listed in `names`. */
export const pickCapabilities = (
  capabilities: EditorCapabilities,
  names: readonly string[],
): EditorCapabilities => {
  const set = new Set(names);
  const out: EditorCapabilities = {};
  if (set.has("heading") && capabilities.heading) {
    out.heading = capabilities.heading;
  }
  if (set.has("bold") && capabilities.bold) out.bold = true;
  if (set.has("italic") && capabilities.italic) out.italic = true;
  if (set.has("underline") && capabilities.underline) out.underline = true;
  if (set.has("strike") && capabilities.strike) out.strike = true;
  if (set.has("bulletList") && capabilities.bulletList) out.bulletList = true;
  if (set.has("orderedList") && capabilities.orderedList) {
    out.orderedList = true;
  }
  if (set.has("link") && capabilities.link) out.link = capabilities.link;
  if (set.has("alignment") && capabilities.alignment) out.alignment = true;
  if (set.has("mention") && capabilities.mentions) {
    out.mentions = capabilities.mentions;
  }
  if (set.has("emoji") && capabilities.emoji) out.emoji = capabilities.emoji;
  if (set.has("image") && capabilities.image) out.image = capabilities.image;
  if (set.has("video") && capabilities.video) out.video = capabilities.video;
  if (set.has("iframe") && capabilities.iframe) out.iframe = true;
  if (set.has("table") && capabilities.table) out.table = true;
  if (set.has("file") && capabilities.file) out.file = capabilities.file;
  return out;
};
